import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import UpcomingEvents from "@/components/UpcomingEvents";
import { CalendarX, AlertTriangle, CalendarClock, Handshake, ShieldAlert, ChevronRight, Users as UsersIcon, Activity, FileText } from "lucide-react";

const StatCard = ({ icon, label, value, tone = "oxford", to, testId }) => {
    const content = (
        <div className={`rounded-2xl border p-4 bg-white flex items-start gap-3 ${to ? "hover:border-oxford hover:shadow-md transition-all cursor-pointer" : ""}`} data-testid={testId}>
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${tone === "oxford" ? "bg-oxford text-white" : tone === "terracotta" ? "bg-terracotta text-white" : tone === "sage" ? "bg-sage text-white" : "bg-seablue text-white"}`}>
                {icon}
            </div>
            <div className="flex-1">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
                <div className="font-heading text-2xl font-black text-slate-900 leading-tight mt-0.5">{value}</div>
            </div>
            {to && <ChevronRight className="w-4 h-4 text-slate-400 self-center" />}
        </div>
    );
    return to ? <Link to={to}>{content}</Link> : content;
};

export default function DrejtoreshaDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        setLoading(true);
        try { const r = await api.get("/drejtoresha/dashboard"); setData(r.data); }
        finally { setLoading(false); }
    };
    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 60000);
        return () => clearInterval(t);
    }, []);

    if (loading && !data) return <Layout><div className="text-slate-500">Duke ngarkuar…</div></Layout>;

    return (
        <Layout>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Paneli i Drejtoreshës</div>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Përmbledhje e ditës</h1>
                <p className="mt-2 text-slate-600 text-sm">Sot: {data?.today} • Muaji: {data?.month} • {data?.total_students} nxënës në total</p>
            </div>

            {/* Top stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <StatCard icon={<CalendarX className="w-5 h-5" />}
                    label="Mungojnë sot"
                    value={`${data?.absent_today?.count || 0} (${data?.absent_today?.hours_total || 0} orë)`}
                    tone="terracotta" testId="dr-stat-absent" />
                <StatCard icon={<AlertTriangle className="w-5 h-5" />}
                    label="Vërejtje"
                    value={data?.warnings_recent?.length || 0}
                    tone="terracotta" testId="dr-stat-warnings" />
                <StatCard icon={<CalendarClock className="w-5 h-5" />}
                    label="Takime"
                    value={data?.upcoming_appointments?.length || 0}
                    to="/administrata/appointments"
                    tone="seablue" testId="dr-stat-appts" />
                <StatCard icon={<ShieldAlert className="w-5 h-5" />}
                    label="Incidente muaji"
                    value={`${data?.security?.today || 0} / ${data?.security?.month || 0}`}
                    to="/sigurimi/reports"
                    tone="oxford" testId="dr-stat-security" />
            </div>

            {/* Quick access tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <Link to="/administrata/appointments" className="bg-white border border-slate-200 hover:border-oxford rounded-xl p-3 flex items-center gap-2 transition" data-testid="dr-tile-appts">
                    <CalendarClock className="w-5 h-5 text-seablue" />
                    <span className="text-sm font-bold text-slate-900">Takimet</span>
                </Link>
                <Link to="/drejtoresha/collaborations" className="bg-white border border-slate-200 hover:border-oxford rounded-xl p-3 flex items-center gap-2 transition" data-testid="dr-tile-collabs">
                    <Handshake className="w-5 h-5 text-sage" />
                    <span className="text-sm font-bold text-slate-900">Bashkëpunime</span>
                </Link>
                <Link to="/sigurimi/reports" className="bg-white border border-slate-200 hover:border-oxford rounded-xl p-3 flex items-center gap-2 transition" data-testid="dr-tile-sigurimi">
                    <ShieldAlert className="w-5 h-5 text-terracotta" />
                    <span className="text-sm font-bold text-slate-900">Sigurimi</span>
                </Link>
                <Link to="/admin/analytics" className="bg-white border border-slate-200 hover:border-oxford rounded-xl p-3 flex items-center gap-2 transition opacity-60" data-testid="dr-tile-analytics">
                    <Activity className="w-5 h-5 text-oxford" />
                    <span className="text-sm font-bold text-slate-900">Analitikat</span>
                </Link>
            </div>

            {/* Two column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                {/* Absent today */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="dr-absent-list">
                    <h2 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <CalendarX className="w-5 h-5 text-terracotta" /> Nxënës që mungojnë sot
                    </h2>
                    {(data?.absent_today?.list || []).length === 0 ? (
                        <div className="text-sm text-slate-500">Askush nuk mungon sot.</div>
                    ) : (
                        <div className="divide-y divide-slate-200 max-h-80 overflow-auto">
                            {data.absent_today.list.map((s) => (
                                <div key={s.student_id} className="py-2 flex items-center gap-2" data-testid={`dr-absent-${s.student_id}`}>
                                    <div className="w-9 h-9 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center text-xs font-bold shrink-0">{s.hours}h</div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-bold text-slate-900 text-sm truncate">{s.name}</div>
                                        <div className="text-[11px] text-slate-500 truncate">{s.class_name || "—"} • {(s.subjects || []).join(", ") || "—"}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Upcoming appointments */}
                <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="dr-appt-list">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                            <CalendarClock className="w-5 h-5 text-seablue" /> Takimet e ardhshme
                        </h2>
                        <Link to="/administrata/appointments" className="text-xs text-oxford font-semibold hover:underline">Shiko të gjitha</Link>
                    </div>
                    {(data?.upcoming_appointments || []).length === 0 ? (
                        <div className="text-sm text-slate-500">Asnjë takim i planifikuar.</div>
                    ) : (
                        <div className="divide-y divide-slate-200 max-h-80 overflow-auto">
                            {data.upcoming_appointments.slice(0, 10).map((a) => (
                                <div key={a.id} className="py-2" data-testid={`dr-appt-${a.id}`}>
                                    <div className="font-bold text-slate-900 text-sm truncate">{a.requester_name}</div>
                                    <div className="text-[11px] text-slate-500">{a.topic}</div>
                                    <div className="text-[11px] text-oxford font-mono mt-0.5">{new Date(a.scheduled_at).toLocaleString("sq-AL")} • {a.duration_minutes}m</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Warnings & Collaborations */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="dr-warnings-list">
                    <h2 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-terracotta" /> Vërejtje të fundit
                    </h2>
                    {(data?.warnings_recent || []).length === 0 ? (
                        <div className="text-sm text-slate-500">Asnjë vërejtje.</div>
                    ) : (
                        <div className="divide-y divide-slate-200 max-h-80 overflow-auto">
                            {data.warnings_recent.slice(0, 10).map((c) => (
                                <div key={c.id} className="py-2" data-testid={`dr-warning-${c.id}`}>
                                    <div className="font-bold text-slate-900 text-sm truncate">{c.student_name || "—"}</div>
                                    <div className="text-[11px] text-slate-500">{c.subject_name || "—"} • {c.date}</div>
                                    <div className="text-[12px] text-slate-700 italic mt-0.5">"{(c.text || "").slice(0, 120)}"</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="dr-collabs-list">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
                            <Handshake className="w-5 h-5 text-sage" /> Bashkëpunime
                        </h2>
                        <Link to="/drejtoresha/collaborations" className="text-xs text-oxford font-semibold hover:underline">Menaxho</Link>
                    </div>
                    {(data?.collaborations || []).length === 0 ? (
                        <div className="text-sm text-slate-500">Asnjë bashkëpunim i regjistruar ende.</div>
                    ) : (
                        <div className="divide-y divide-slate-200 max-h-80 overflow-auto">
                            {data.collaborations.slice(0, 10).map((c) => (
                                <div key={c.id} className="py-2" data-testid={`dr-collab-${c.id}`}>
                                    <div className="font-bold text-slate-900 text-sm truncate">{c.name}</div>
                                    <div className="text-[11px] text-slate-500">
                                        {c.type === "institution" ? "Institucion" : c.type === "company" ? "Kompani" : c.type === "university" ? "Universitet" : c.type === "ngo" ? "OJQ" : "Tjetër"}
                                        {c.contact_person ? ` • ${c.contact_person}` : ""}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <UpcomingEvents days={21} limit={6} />
        </Layout>
    );
}
